"use client";

import { INPUT, LABEL, SELECT } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import type {
  LogColumn,
  LogEntries,
} from "@/lib/tool-kit/printables/reading-log";

/** The grid of boxes for typing log entries on screen; they print into the sheet. */
export function EntriesGrid({
  columns,
  heads,
  rows,
  entries,
  onChange,
  cellLabel,
  ratingNone,
}: {
  columns: readonly LogColumn[];
  heads: Record<LogColumn, string>;
  rows: number;
  entries: LogEntries;
  onChange: (row: number, key: LogColumn, value: string) => void;
  /** "{column}, row {row}" — the accessible name of each box. */
  cellLabel: string;
  ratingNone: string;
}) {
  const typed = columns.filter((key) => key !== "initials");
  return (
    <div className="max-h-96 overflow-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 bg-muted">
          <tr>
            {typed.map((key) => (
              <th
                className={`${LABEL} px-2 py-2 text-left`}
                key={key}
                scope="col"
              >
                {heads[key]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, row) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional
            <tr key={row}>
              {typed.map((key) => {
                const label = t(cellLabel, {
                  column: heads[key],
                  row: row + 1,
                });
                return (
                  <td className="px-1 py-1" key={key}>
                    {key === "rating" ? (
                      <select
                        aria-label={label}
                        className={SELECT}
                        onChange={(event) =>
                          onChange(row, key, event.target.value)
                        }
                        value={entries[row]?.rating ?? "0"}
                      >
                        <option value="0">{ratingNone}</option>
                        {[1, 2, 3, 4, 5].map((value) => (
                          <option key={value} value={String(value)}>
                            {"★".repeat(value)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        aria-label={label}
                        className={INPUT}
                        onChange={(event) =>
                          onChange(row, key, event.target.value)
                        }
                        type="text"
                        value={entries[row]?.[key] ?? ""}
                      />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
